// === AANSPRAKELIJKHEID SCREEN WITH CUSTOM FIELDS ===
// Complete Aansprakelijkheid insurance product screen
// Similar structure to WoonhuisScreen.tsx and InboedelScreen.tsx

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  useWorkflow,
  useSharedAddress,
  useConfig,
  WorkflowPageLayout,
  bffClient,
  LoadingScreen,
  computeAvpDisplayPremium,
  getConfigPathForBrand,
  getCurrentBrand,
} from '@sluitstraat/core';
import { getApplicationName } from '../../utils/appName';
import { setIn } from '../../utils/setIn';
import { commonStyles } from '../../styles/commonStyles';
import { AddressSection, Address } from '../../components/AddressSection';
import { PersonalInfoSection } from '../../components/PersonalInfoSection';
import { DebugPanel } from '../../components/DebugPanel';
import { WithHelpSidebar } from '../../components/HelpCard';
import { WorkflowNavButtons } from '../../components/WorkflowNavButtons';
import { useWorkflowNav } from '../../hooks/useWorkflowNav';
import { CoverageOptionsCard } from '../../components/CoverageOptionsCard';
import { findUpstreamAddress, shouldHideAddressSection, syncAddressToOtherProducts } from '../../utils/addressSharing';
import { findUpstreamFamilyComposition, findSharedFamilyComposition, readFamilyComposition, syncFamilyCompositionToOtherProducts } from '../../utils/familyComposition';
import { useSharedFamilyComposition } from '../../hooks/useSharedFamilyComposition';
import {
  extractDefaults,
  deepMerge,
  cleanAvpForRequest,
  applyProductDefaults,
  linkRelationData,
  AVP_PRODUCT,
  validateAvp,
  areAvpRequiredFieldsFilled,
  avpCalcInputSignature,
  scrollToFirstError,
  stripResponseBa,
  detectAvpRejection,
  mergeAvpResult,
  applyAvpDiscount,
} from './aansprakelijkheid';

const SCREEN_ID = 'aansprakelijkheid';


export const AansprakelijkheidScreen: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { getStepData, saveStepData, selectedProducts, relationData } = useWorkflow();
  const { config } = useConfig();
  // Keep a ref to config so handleCalculate always reads the latest (avoids stale closure)
  const configRef = useRef(config);
  configRef.current = config;

  // Schema and loading states
  const [loading, setLoading] = useState(true);
  const [schemaLoaded, setSchemaLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // Data state - use lazy initializer to prevent re-running during re-renders
  const [data, setData] = useState<any>(() => getStepData(SCREEN_ID) || {});

  // Calculation states
  const [isCalculating, setIsCalculating] = useState(false);
  const [calculationError, setCalculationError] = useState<string | null>(null);
  const [lastCalculationInput, setLastCalculationInput] = useState<any>(null);
  const [lastCalculationOutput, setLastCalculationOutput] = useState<any>(null);

  const upstreamGss = findUpstreamFamilyComposition(getStepData, SCREEN_ID, selectedProducts);
  const hideFamilyComposition = !!upstreamGss;

  // Single source of truth for the family composition across every product (see the hook).
  const { familyComposition, setFamilyComposition } = useSharedFamilyComposition();

  // Migrate a value entered before the shared store existed / on a since-deselected product.
  useEffect(() => {
    if (!familyComposition) {
      const found = findSharedFamilyComposition(getStepData);
      if (found) setFamilyComposition(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyComposition]);

  // Mirror the shared value into AVP's own DA_GSS so the BFF payload carries it.
  useEffect(() => {
    if (!familyComposition) return;
    setData((prev: any) => {
      if (prev?.Contractdocument?.PP?.DA?.DA_GSS === familyComposition) return prev;
      const updated = {
        ...prev,
        Contractdocument: {
          ...prev?.Contractdocument,
          PP: {
            ...prev?.Contractdocument?.PP,
            DA: { ...prev?.Contractdocument?.PP?.DA, DA_GSS: familyComposition },
          },
        },
      };
      setTimeout(() => saveStepData(SCREEN_ID, updated), 0);
      return updated;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [familyComposition]);

  const { address: sharedAddress } = useSharedAddress();
  const lastCalcAddrKey = useRef<string>('');
  const lastCalcSignature = useRef<string>('');

  // Validate required data on mount - redirect if missing critical data
  useEffect(() => {
    // Check if aansprakelijkheid product is selected
    if (!selectedProducts?.aansprakelijkheid) {
      navigate('/workflow/product-selection', { replace: true });
      return;
    }

    // Check if client information exists
    const clientInfo = relationData?.clientInfo;
    if (!clientInfo || !clientInfo.email || !clientInfo.initials || !clientInfo.lastName) {
      navigate('/workflow/client-information', { replace: true });
      return;
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only guard; re-running on selectedProducts/navigate identity would redirect mid-flow
  }, []); // Run once on mount

  // Load schema and apply defaults (but preserve existing address data)
  useEffect(() => {
    // Prevent re-running if already loaded
    if (schemaLoaded) return;

    const loadSchema = async () => {
      try {
        const response = await fetch(`/screens/${SCREEN_ID}.json`);
        if (!response.ok) {
          throw new Error(`Failed to load schema for ${SCREEN_ID}`);
        }

        const screenData = await response.json();
        const loadedSchema = screenData.jsonFormSchema;

        setSchemaLoaded(true);

        // Extract defaults from schema
        const schemaDefaults = extractDefaults(loadedSchema);

        // Get existing data from context (includes any saved address data)
        const existingData = getStepData(SCREEN_ID) || {};

        const existingPp = existingData?.Contractdocument?.PP;
        if (existingPp?.DA?.DA_TTOT || existingPp?.PP_TTOT) {
          lastCalcSignature.current = avpCalcInputSignature(existingData);
        }

        // Deep merge: existing data takes precedence over schema defaults
        const mergedData = deepMerge(existingData, schemaDefaults);

        // Apply product-specific defaults (appName/baseUrl/now injected; calc.ts stays pure)
        const appName = await getApplicationName('Insurance Application');
        const now = new Date();
        const dataWithProductDefaults = applyProductDefaults(mergedData, { appName, baseUrl: window.location.origin + '/', now });

        // Link relation data from client-information page (preserves existing address)
        const dataWithRelationData = linkRelationData(dataWithProductDefaults, relationData, { now });

        // Address is a single piece of data tied to the policyholder, not to each product.
        // sharedAddress is the canonical source of truth — always overwrite local BA from it
        // when populated, so an edit made elsewhere (RBV, etc.) is reflected here on every
        // visit, even if AVP step data still has an older copy. Fall back to scanning other
        // products' step data for legacy sessions where sharedAddress hasn't been seeded yet.
        let finalData = dataWithRelationData;
        const sourceBA = (sharedAddress?.BA_PCODE && sharedAddress?.BA_HUISNR)
          ? sharedAddress
          : findUpstreamAddress(getStepData, SCREEN_ID);

        if (sourceBA?.BA_PCODE && sourceBA?.BA_HUISNR) {
          finalData = {
            ...finalData,
            Contractdocument: {
              ...finalData.Contractdocument,
              PP: {
                ...finalData.Contractdocument?.PP,
                BA: {
                  ...finalData.Contractdocument?.PP?.BA,
                  BA_PCODE: sourceBA.BA_PCODE,
                  BA_HUISNR: sourceBA.BA_HUISNR,
                  BA_TOEVOEG: sourceBA.BA_TOEVOEG || '',
                  BA_STRAAT: sourceBA.BA_STRAAT || '',
                  BA_PLAATS: sourceBA.BA_PLAATS || ''
                }
              }
            }
          };
        }

        // Seed Gezinssamenstelling from any selected product (any *_GSS field) so the field is
        // prefilled and the first calc already has it — the value is picked once and shared,
        // regardless of workflow order (AVP sits before RBV/DR). Keep AVP's own value if present.
        const existingGSS = finalData.Contractdocument?.PP?.DA?.DA_GSS;
        const seedGSS = existingGSS || findSharedFamilyComposition(getStepData);
        if (seedGSS) {
          if (!finalData.Contractdocument?.PP?.DA) {
            finalData.Contractdocument.PP.DA = {};
          }
          finalData.Contractdocument.PP.DA.DA_GSS = seedGSS;
        }

        // Set final data
        setData(finalData);

        // Save the resolved data so AVP's persisted slice always matches the canonical
        // address (even when step data already had an older copy and we just overwrote it).
        if (finalData !== dataWithRelationData) {
          saveStepData(SCREEN_ID, finalData);
        }

        setLoading(false);

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unknown error');
        setLoading(false);
      }
    };

    loadSchema();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- mount-only schema load; its inputs are read once
  }, [schemaLoaded]);

  // Seed the recalc dedup key when an already-resolved address (street + city) is
  // restored, so the first emit from <AddressSection> doesn't fire a duplicate recalc.
  useEffect(() => {
    if (!loading && data && !lastCalcAddrKey.current) {
      const ba = data?.Contractdocument?.PP?.BA;
      if (ba?.BA_PCODE && ba?.BA_HUISNR && ba?.BA_STRAAT && ba?.BA_PLAATS) {
        lastCalcAddrKey.current = `${ba.BA_PCODE}|${ba.BA_HUISNR}|${ba.BA_TOEVOEG || ''}`;
      }
    }
  }, [loading, data]);

  useEffect(() => {
    if (!loading && data && areAllRequiredFieldsFilled(data)
      && avpCalcInputSignature(data) !== lastCalcSignature.current) {
      handleCalculate(data);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- one-shot initial calculation after load; handleCalculate/data are read at that moment
  }, [loading]); // Only run once after initial load

  // Check if all required fields are filled
  const areAllRequiredFieldsFilled = useCallback((d: any): boolean => areAvpRequiredFieldsFilled(d), []);

  // Handle non-address field changes (e.g. Gezinssamenstelling DA_GSS). The address
  // is owned by <AddressSection> and persisted via handleAddressFromComponent.
  const handleCustomFieldChange = (field: string, value: any) => {
    if (validationErrors[field]) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }

    if (hideFamilyComposition && field === 'Contractdocument.PP.DA.DA_GSS') {
      return;
    }

    const updatedData = setIn(data, field.split('.'), value);
    setData(updatedData);

    // Family composition is picked once and shared: write it to the single shared store (the
    // source of truth every product reads) and mirror it into the other products' step data too.
    if (/(_GSS|GEZSAMST)$/i.test(field) && value) {
      setFamilyComposition(value);
      saveStepData(SCREEN_ID, updatedData);
      syncFamilyCompositionToOtherProducts(SCREEN_ID, value, selectedProducts, getStepData, saveStepData);
    }

    if (areAllRequiredFieldsFilled(updatedData)) {
      handleCalculate(updatedData);
    }
  };

  // Persist the address emitted by <AddressSection>: mirror it into BA + the
  // policyholder VP fields, keep the other selected products in sync, and
  // recalculate once a fully-resolved address (street + city) settles.
  const handleAddressFromComponent = useCallback((a: Address) => {
    const prevBA = data?.Contractdocument?.PP?.BA || {};
    const pcodeChanged = (prevBA.BA_PCODE || '') !== (a.postcode || '');
    const huisnrChanged = String(prevBA.BA_HUISNR ?? '') !== String(a.houseNumber ?? '');
    const huisNr = Number(a.houseNumber);
    const vpHuisnr = isNaN(huisNr) ? 0 : huisNr;

    const updatedData: any = {
      ...data,
      Contractdocument: {
        ...data?.Contractdocument,
        PP: {
          ...data?.Contractdocument?.PP,
          BA: {
            ...prevBA,
            BA_PCODE: a.postcode,
            BA_HUISNR: a.houseNumber,
            BA_TOEVOEG: a.addition || '',
            BA_STRAAT: a.street || '',
            BA_PLAATS: a.city || '',
          },
          VP: {
            ...data?.Contractdocument?.PP?.VP,
            VP_PCODE: a.postcode,
            VP_HUISNR: vpHuisnr,
            VP_TOEVOEG: a.addition || '',
            VP_STRAAT: a.street || '',
            VP_PLAATS: a.city || '',
          },
        },
      },
    };

    if ((pcodeChanged || huisnrChanged) && (!a.street || !a.city)) {
      if (updatedData.Contractdocument?.PP?.DA?.DA_TTOT !== undefined) {
        delete updatedData.Contractdocument.PP.DA.DA_TTOT;
      }
      setCalculationError(null);
    }

    setData(updatedData);
    saveStepData(SCREEN_ID, updatedData);

    syncAddressToOtherProducts(
      SCREEN_ID,
      {
        BA_PCODE: a.postcode,
        BA_HUISNR: a.houseNumber,
        BA_TOEVOEG: a.addition || '',
        BA_STRAAT: a.street || '',
        BA_PLAATS: a.city || '',
      },
      selectedProducts,
      getStepData,
      saveStepData,
    );

    const addrKey = `${a.postcode}|${a.houseNumber}|${a.addition || ''}`;
    if (a.street && a.city && addrKey !== lastCalcAddrKey.current && areAllRequiredFieldsFilled(updatedData)) {
      lastCalcAddrKey.current = addrKey;
      handleCalculate(updatedData);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, selectedProducts, getStepData, saveStepData, areAllRequiredFieldsFilled]);

  // Mapping of validation error keys to form element IDs

  // Manual calculation trigger (from debug panel)
  const handleManualCalculate = () => {
    const { isValid, errors } = validateForm();
    if (!isValid) {
      setCalculationError(t('common.fillRequiredFields'));
      // Scroll to first field with validation error
      scrollToFirstError(errors);
      return;
    }
    handleCalculate(data);
  };

  // Call BFF calculation
  const handleCalculate = useCallback(async (dataToCalculate?: any) => {
    const calculationData = dataToCalculate || data;

    // Address must be resolved (street + city) before calculating.
    const ba = calculationData?.Contractdocument?.PP?.BA;
    if (!ba?.BA_STRAAT || !ba?.BA_PLAATS) {
      setCalculationError(t('common.addressInvalid'));
      setValidationErrors(prev => ({
        ...prev,
        '_addressValidation': 'screens.aansprakelijkheid.validation.addressNotValidated'
      }));
      return;
    }

    try {
      setIsCalculating(true);
      setCalculationError(null);

      // Ensure VP fields are set for calculation
      let enrichedData = JSON.parse(JSON.stringify(calculationData));
      // Re-link client information so VP personal fields (VP_GEBDAT, VP_VOORL, VP_ANAAM, VP_VOORV)
      // are always present at request time. linkRelationData runs on mount but a previous failed
      // calculate response that got merged into `data` (or stale localStorage) can drop these
      // fields, leading to BFF 400s like "Verplicht veld ontbreekt: geboortedatum (vp_gebdat)".
      enrichedData = linkRelationData(enrichedData, relationData, { now: new Date() });
      const postalCode = enrichedData?.Contractdocument?.PP?.BA?.BA_PCODE;
      if (postalCode) {
        enrichedData.Contractdocument.PP.VP = {
          ...enrichedData.Contractdocument.PP.VP,
          VP_PCODE: postalCode,
          VP_PARZAKC: "P"
        };
      }

      // Required-field defaults (PP_STPRV, PP_BETTERM, DA_CODE, …) are now applied inside
      // cleanAvpForRequest so a single source of truth fills any gaps from stale state.

      // Clean data to match request schema: whitelist allowed fields,
      // strip extras (BA, VRWRKCD, CVS, etc.), coerce types, add required defaults
      const cleanedData = cleanAvpForRequest(enrichedData, { t, now: new Date() });

      // Build request body according to BFF specification
      const requestBody = {
        carrierCode: AVP_PRODUCT.carrierCode,
        productName: AVP_PRODUCT.productName,
        input: cleanedData
      };

      // Store input (the wrapped request)
      setLastCalculationInput(requestBody);

      const bffResult = await bffClient.request<any>(
        'POST',
        '/api/v1/productservice/calculate',
        requestBody
      );

      // Strip BA from the response (AVP address comes from Infofolio, not ProductService).
      const result = stripResponseBa(bffResult);

      if (result && result.output) {
        // Store raw BFF response for debug panel (before any post-processing)
        setLastCalculationOutput(bffResult);

        // Always use raw undiscounted response for data state and workflow persistence
        const responseData = JSON.parse(JSON.stringify(result.output));

        // BFF returns 200 + XG.XM error list on reject; bail before merge so the
        // applyProductDefaults values (PP_MYAAND, DA_CODE, …) survive for the next attempt.
        const rejection = detectAvpRejection(responseData);
        if (rejection) {
          setCalculationError(rejection);
          setIsCalculating(false);
          return;
        }

        const mergedData = mergeAvpResult(responseData, data);

        // Bake the AVP discount into the data itself (DA + PP premium fields).
        // Previously this was a UI-only overlay; it's now applied to the
        // persisted data so downstream screens consume already-discounted values.
        let avpDiscountCfg = (configRef.current as any)?.avpDiscount;
        if (!avpDiscountCfg) {
          try {
            const cfgRes = await fetch(getConfigPathForBrand(getCurrentBrand()));
            const cfg = await cfgRes.json();
            avpDiscountCfg = cfg?.avpDiscount;
          } catch (_) {}
        }
        if (avpDiscountCfg?.enabled && avpDiscountCfg?.percentage > 0 && avpDiscountCfg?.applyToData !== false) {
          applyAvpDiscount(mergedData, avpDiscountCfg.percentage);
        }

        setData(mergedData);
        saveStepData(SCREEN_ID, mergedData);
        lastCalcSignature.current = avpCalcInputSignature(mergedData);
      }

      setIsCalculating(false);
    } catch (error) {
      setCalculationError(error instanceof Error ? error.message : 'Calculation failed');
      setLastCalculationOutput({ error: error instanceof Error ? error.message : 'Calculation failed' });
      setIsCalculating(false);
    }
  }, [data, relationData, saveStepData, t]);

  // Get validation errors (without side effects)
  // Returns translation keys, not translated strings, so errors update when language changes
  const getValidationErrors = useCallback((): Record<string, string> => validateAvp(data), [data]);

  // Validate form fields (with side effect of setting errors)
  const validateForm = useCallback((): { isValid: boolean; errors: Record<string, string> } => {
    const errors = getValidationErrors();
    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return { isValid: false, errors };
    }

    return { isValid: true, errors: {} };
  }, [getValidationErrors]);


  // Re-translate existing validation errors when language changes
  useEffect(() => {
    // Only re-validate if there are existing errors (to update their translations)
    setValidationErrors(prevErrors => {
      if (Object.keys(prevErrors).length === 0) {
        return prevErrors;
      }
      // Re-validate to get translated messages
      const errors = getValidationErrors();
      return errors;
    });
  }, [t, getValidationErrors]);

  const { currentStep, totalSteps, productIndex, totalProducts, goToNext, goToPrevious } = useWorkflowNav(SCREEN_ID);

  // When the user clicks Volgende while a premium calc is still running, remember to navigate
  // once the calc finishes (it persists the premium on completion). Without this the first
  // click mid-calc is swallowed, so the user has to click twice.
  const pendingNavRef = useRef<boolean>(false);

  const handleNext = () => {
    const { isValid, errors } = validateForm();
    if (!isValid) {
      scrollToFirstError(errors);
      return;
    }
    if (isCalculating) {
      pendingNavRef.current = true;
      return;
    }

    saveStepData(SCREEN_ID, data);
    goToNext();
  };

  useEffect(() => {
    if (!isCalculating && pendingNavRef.current) {
      pendingNavRef.current = false;
      goToNext();
    }
  }, [isCalculating, goToNext]);

  const handlePrevious = () => {
    saveStepData(SCREEN_ID, data);
    goToPrevious();
  };

  // Loading state
  if (loading) {
    return <LoadingScreen message={t('common.loadingSchema')} />;
  }

  // Error state
  if (error) {
    return (
      <div style={commonStyles.errorContainer}>
        <h2 style={commonStyles.errorTitle}>{t('common.errorLoadingSchema')}</h2>
        <p>{error}</p>
        <button onClick={() => navigate('/')} style={commonStyles.errorButton}>
          {t('common.backToHome')}
        </button>
      </div>
    );
  }

  return (
    <WorkflowPageLayout currentStep={currentStep} totalSteps={totalSteps}>
      <main style={commonStyles.page}>
        <div style={commonStyles.container}>
          <WithHelpSidebar stackEarly>
            <header style={commonStyles.contentHeader}>
              <h1 style={commonStyles.title}>{t('screens.aansprakelijkheid.title')}</h1>
            </header>

            {/* Form layout */}

            <div style={commonStyles.formColumn}>

          {/* Address Section — hidden when an upstream selected product already supplied the address */}
          {!shouldHideAddressSection('aansprakelijkheid', selectedProducts, getStepData) && (
            <AddressSection
              value={{
                postcode: data?.Contractdocument?.PP?.BA?.BA_PCODE || '',
                houseNumber: data?.Contractdocument?.PP?.BA?.BA_HUISNR != null ? String(data.Contractdocument.PP.BA.BA_HUISNR) : '',
                addition: data?.Contractdocument?.PP?.BA?.BA_TOEVOEG || '',
                street: data?.Contractdocument?.PP?.BA?.BA_STRAAT || '',
                city: data?.Contractdocument?.PP?.BA?.BA_PLAATS || '',
              }}
              onChange={handleAddressFromComponent}
            />
          )}

          <PersonalInfoSection
            id="gezin"
            value={familyComposition || data?.Contractdocument?.PP?.DA?.DA_GSS || readFamilyComposition(data) || ''}
            onChange={(v) => handleCustomFieldChange('Contractdocument.PP.DA.DA_GSS', v)}
            hideFamilyField={hideFamilyComposition}
            error={validationErrors['Contractdocument.PP.DA.DA_GSS'] ? t(validationErrors['Contractdocument.PP.DA.DA_GSS']) : null}
          />

          <CoverageOptionsCard
            title={t('screens.aansprakelijkheid.sections.coverageOptions')}
            totalLabel={t('screens.aansprakelijkheid.sections.total')}
            total={computeAvpDisplayPremium(data, config as any)}
            calculating={isCalculating}
            items={[
              {
                label: t('screens.aansprakelijkheid.coverageInfo.liability.title'),
                descriptions: [1, 2, 3, 4, 5].map((n) => t(`screens.aansprakelijkheid.coverageInfo.liability.item${n}`)),
                amount: computeAvpDisplayPremium(data, config as any),
              },
            ]}
          />

          {calculationError && (
            <div style={commonStyles.inlineErrorBanner}>{calculationError}</div>
          )}

            </div> {/* End formColumn */}

            {/* Next stays clickable during a running calc — handleNext queues the navigation. */}
            <WorkflowNavButtons
              onBack={handlePrevious}
              onNext={handleNext}
              nextLabel={isCalculating ? 'screens.aansprakelijkheid.calculating' : 'common.nextPlain'}
              center={
                <div style={commonStyles.productCounter}>
                  {t('common.productCounter', { current: productIndex, total: totalProducts })}
                </div>
              }
            />
          </WithHelpSidebar>

          {/* Debug panel — shared component, dev-mode only */}
          <DebugPanel
            input={lastCalculationInput}
            output={lastCalculationOutput}
            onRecalculate={handleManualCalculate}
            calculating={isCalculating}
            validationErrors={validationErrors}
          />

        </div>
      </main>
    </WorkflowPageLayout>
  );
};


export default AansprakelijkheidScreen;
